import React from "react";
import { useTranslation } from "react-i18next";
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import "./index.css";
import InfoIcon from "../../../components/InfoIcon";

const MassBalanceTable = ({ data }) => {
  const { t } = useTranslation();

  if (!data || !data.phases || data.phases.length === 0) {
    return null;
  }

  const MassBalanceTablePhase = ({ phase }) => {
    return (
      <div className="mass-balance-phase-section">
        {/* Phase Label */}
        <div className="mass-balance-phase-label">
          {phase.phaseLabel || phase.phase}
        </div>

        <div className="mass-balance-tables-row">
          {/* Input Table */}
          <div className="mass-balance-table-container">
            <table className="mass-balance-table">
              <thead>
                <tr className="mass-balance-table-header mass-balance-input-header">
                  <th className="mass-balance-table-col-number">#</th>
                  <th className="mass-balance-table-col-material">
                    {t("MATERIAL_COMPONENT") || "Material / Component"}
                  </th>
                  <th className="mass-balance-table-col-amount">
                    <div className="mass-balance-amount-header">
                      {t("AMOUNT") || "Amount"}
                      <span className="mass-balance-header-badge mass-balance-input-badge">
                        {t("INPUT") || "Input"}
                      </span>
                    </div>
                  </th>
                  <th className="mass-balance-table-col-unit">{t("UNIT") || "Unit"}</th>
                </tr>
              </thead>
              <tbody>
                {phase.inputs.map((row, idx) => {
                  // Calculate row number - skip header rows (empty label or ends with ":") and summary rows
                  const isHeaderRow = !row.label || row.label.trim() === "" || row.label.endsWith(":");
                  // Count only data rows (not header or summary rows) for numbering
                  const dataRowIndex = phase.inputs.slice(0, idx).filter(r => {
                    const isHdr = !r.label || r.label.trim() === "" || r.label.endsWith(":");
                    return !isHdr && !r.isSummary;
                  }).length;
                  const rowNumber = isHeaderRow || row.isSummary ? "" : dataRowIndex + 1;
                  
                  return (
                  <tr
                    key={row.id}
                    className={`mass-balance-table-row ${
                      row.isSummary ? "mass-balance-summary-row" : idx % 2 === 0 ? "mass-balance-row-even" : "mass-balance-row-odd"
                    }`}
                  >
                    <td className="mass-balance-table-col-number">
                      {rowNumber}
                    </td>
                    <td className="mass-balance-table-col-material">
                      <div className="mass-balance-material-cell">
                        <span>{row.label}</span>
                        {row.tooltip && (
                          <OverlayTrigger
                            placement="right"
                            overlay={
                              <Tooltip id={`tooltip-input-${row.id}`}>
                                <div className="mass-balance-tooltip">
                                  <div className="mass-balance-tooltip-header">
                                    {t("FLOW_REFERENCE") || "Flow reference"}
                                  </div>
                                  <div className="mass-balance-tooltip-body">
                                    {row.tooltip}
                                  </div>
                                </div>
                              </Tooltip>
                            }
                          >
                            <button className="mass-balance-info-button">
                              <InfoIcon />
                            </button>
                          </OverlayTrigger>
                        )}
                      </div>
                    </td>
                    <td className="mass-balance-table-col-amount">
                      {row.value && (
                        <span className={`mass-balance-value ${row.isSummary ? "mass-balance-summary-value" : ""}`}>
                          {row.value}
                        </span>
                      )}
                      {row.flowRef && !row.value && (
                        <span className="mass-balance-flow-ref">{row.flowRef}</span>
                      )}
                    </td>
                    <td className="mass-balance-table-col-unit">{row.unit}</td>
                  </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Output Table */}
          <div className="mass-balance-table-container">
            <table className="mass-balance-table">
              <thead>
                <tr className="mass-balance-table-header mass-balance-output-header">
                  <th className="mass-balance-table-col-number">#</th>
                  <th className="mass-balance-table-col-material">
                    {t("MATERIAL_COMPONENT") || "Material / Component"}
                  </th>
                  <th className="mass-balance-table-col-amount">
                    <div className="mass-balance-amount-header">
                      {t("AMOUNT") || "Amount"}
                      <span className="mass-balance-header-badge mass-balance-output-badge">
                        {t("OUTPUT") || "Output"}
                      </span>
                    </div>
                  </th>
                  <th className="mass-balance-table-col-unit">{t("UNIT") || "Unit"}</th>
                </tr>
              </thead>
              <tbody>
                {phase.outputs.map((row, idx) => {
                  // Calculate row number - skip header rows (empty label or ends with ":") and summary rows
                  const isHeaderRow = !row.label || row.label.trim() === "" || row.label.endsWith(":");
                  // Count only data rows (not header or summary rows) for numbering
                  const dataRowIndex = phase.outputs.slice(0, idx).filter(r => {
                    const isHdr = !r.label || r.label.trim() === "" || r.label.endsWith(":");
                    return !isHdr && !r.isSummary;
                  }).length;
                  const rowNumber = isHeaderRow || row.isSummary ? "" : dataRowIndex + 1;
                  
                  return (
                  <tr
                    key={row.id}
                    className={`mass-balance-table-row ${
                      row.isSummary ? "mass-balance-summary-row" : idx % 2 === 0 ? "mass-balance-row-even" : "mass-balance-row-odd"
                    }`}
                  >
                    <td className="mass-balance-table-col-number">
                      {rowNumber}
                    </td>
                    <td className="mass-balance-table-col-material">
                      <div className="mass-balance-material-cell">
                        <span>{row.label}</span>
                        {row.tooltip && (
                          <OverlayTrigger
                            placement="right"
                            overlay={
                              <Tooltip id={`tooltip-output-${row.id}`}>
                                <div className="mass-balance-tooltip">
                                  <div className="mass-balance-tooltip-header">
                                    {t("FLOW_REFERENCE") || "Flow reference"}
                                  </div>
                                  <div className="mass-balance-tooltip-body">
                                    {row.tooltip}
                                  </div>
                                </div>
                              </Tooltip>
                            }
                          >
                            <button className="mass-balance-info-button">
                              <InfoIcon />
                            </button>
                          </OverlayTrigger>
                        )}
                      </div>
                    </td>
                    <td className="mass-balance-table-col-amount">
                      {row.value && (
                        <span className={`mass-balance-value ${row.isSummary ? "mass-balance-summary-value" : ""}`}>
                          {row.value}
                        </span>
                      )}
                      {row.flowRef && !row.value && (
                        <span className="mass-balance-flow-ref">{row.flowRef}</span>
                      )}
                    </td>
                    <td className="mass-balance-table-col-unit">{row.unit}</td>
                  </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="mass-balance-tables-container">
      {data.phases.map((phase) => (
        <MassBalanceTablePhase key={phase.phase} phase={phase} />
      ))}
    </div>
  );
};

export default MassBalanceTable;

